import fareOptimizerResource from '@/api/resources/fareOptimizer'
import energyInvoiceResource from '@/api/resources/energyInvoice'
import energyConsumerUnitResource from '@/api/resources/energyConsumerUnit'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { getDisplayInfo, isValueWithinErrorMargin } from '@/composable/useToolsFareOptimizer'
import {
  IFareReportParams,
  IFreeMarketAnalysisParams,
  IFreeMarketAnalysisResponse,
  IExtractPdfDataResponse,
  IPdfBase64Params,
  IPeakWasteAnalysis,
  IReportOpportunity,
  IWaterfallChartBar,
} from '@/interfaces/api/fareOptimizer'
import {
  buildDefaultOpportunities,
  buildOpportunity,
  buildWaterfallChartBars,
  buildWaterfallFreeMarketBar,
  buildWaterfallOpportunityBars,
  buildWaterfallPeakWasteBar,
  withPeakWasteOpportunity,
} from '@/utils/reportOpportunities'
import { ICreateEnergyConsumerUnitParams, IUpdateEnergyConsumerUnitParams } from '@/interfaces/api/energyConsumerUnit'
import { IManualInvoiceParams, ISaveInvoiceParams } from '@/interfaces/api/energyInvoice'
import { IEnergyConsumerUnit } from '@/interfaces/entities/energyConsumerUnit'
import { IEnergyInvoice, IEnergyInvoiceSummary } from '@/interfaces/entities/energyInvoice'
import { IDisplayItem } from '@/interfaces/components/fareOptimizer/displayItems'
import { createFareItems, FareItemKey, FareItems, ItemDescription, HistoricalDataItem, createHistoricalDataList } from '@/interfaces/entities/fareItems'
import { i18n } from '@/plugins/i18n'
import { useErrorStore } from '@/store/error'
import { reportErrorMessage } from '@/services/report-error'
import { AnalyticsEvents, logFirebaseAnalytics } from '@/utils/firebase'
import { useCompanyStore } from '@/store/company'
import { defineStore } from 'pinia'

function isDataArrayEnvelope(body: unknown): body is { data: unknown[] } {
  return !!body && typeof body === 'object' && Array.isArray((body as { data?: unknown }).data)
}

function isDataRecordEnvelope(body: unknown): body is { data: object } {
  return (
    !!body &&
    typeof body === 'object' &&
    'data' in body &&
    !!(body as { data: unknown }).data &&
    typeof (body as { data: unknown }).data === 'object'
  )
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[]
  if (isDataArrayEnvelope(body)) return body.data as T[]
  return []
}

function unwrapRecord<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null
  if (isDataRecordEnvelope(body)) return body.data as T
  return body as T
}

const calculateItemValue = (item?: ItemDescription): number => {
  if (!item) return 0
  return item.amount * item.price_per_unit || 0
}

export const useFareOptimizerStore = defineStore({
  id: 'fareOptimizer',
  // Não persistimos pdfBase64 (pode ter vários MB e estourar o localStorage)
  // nem os flags transitórios de loading.
  persist: {
    // savedInvoices/selectedInvoice vêm do servidor — não persistir em localStorage.
    paths: ['fareItems', 'historicalData', 'freeMarketResult', 'validationAlerts', 'displayItems', 'referenceMonth', 'distribuidora', 'curatedOpportunities', 'waterfallItems', 'peakWasteAnalysis'],
  },

  state: () => ({
    fareItems: createFareItems(),
    historicalData: [] as HistoricalDataItem[],
    peakWasteAnalysis: null as IPeakWasteAnalysis | null,
    waterfallItems: [] as string[],
    freeMarketResult: {} as IFreeMarketAnalysisResponse,
    isLoadingOpportunity: false,
    validationAlerts: [] as string[],
    displayItems: [] as IDisplayItem[],
    // base64 da fatura original, necessário para o backend renderizar o relatório
    pdfBase64: '' as string,
    isGeneratingReport: false,
    // --- Persistência da fatura (mês de referência + metadados) ---
    referenceMonth: '' as string, // 'YYYY-MM'
    distribuidora: '' as string,
    isSavingInvoice: false,
    savedInvoices: [] as IEnergyInvoiceSummary[],
    isLoadingInvoices: false,
    selectedInvoice: null as IEnergyInvoice | null,
    
    consumerUnits: [] as IEnergyConsumerUnit[],
    selectedConsumerUnitId: null as number | null,
    isLoadingConsumerUnits: false,
    // Oportunidades curadas pelo diretor (inclui/remove/edita itens da fatura).
    // null = ainda não tocadas → usamos a lista auto-detectada (reportOpportunities).
    curatedOpportunities: null as IReportOpportunity[] | null,
  }),

  getters: {
    tariffTotalAPI: (state) => {
      return state.fareItems.tariff_total?.value ?? 0
    },

    tariffTotalTable: (state) => {
      return state.displayItems.reduce((acc, item) => {
        if (item.amount === 0 && item.pricePerUnit === 0) {
          return acc + item.valueFromAPI
        } else {
          return acc + item.amount * item.pricePerUnit
        }
      }, 0)
    },

    tableItems: (state) => {
      return state.displayItems
    },

    tableFreeMarket: (state) => {
      const { free_market } = state.freeMarketResult
      return free_market?.table
    },

    getInvoiceAlerts: (state) => {
      return state.validationAlerts
    },

    deltaFreeMarket: (state) => {
      const { free_market } = state.freeMarketResult
      const usedConsumption = calculateItemValue(state.fareItems.peak_consumption)
      const usedPeakConsumption = calculateItemValue(state.fareItems.off_peak_consumption)
      const measuredPeakDemand = calculateItemValue(state.fareItems.measured_peak_demand)
      const measuredOffPeakDemand = calculateItemValue(state.fareItems.measured_off_peak_demand)

      return usedConsumption + usedPeakConsumption + measuredPeakDemand + measuredOffPeakDemand - (free_market?.value || 0)
    },

    isLoadingOpportunityCalc: (state) => state.isLoadingOpportunity,

    hasOverdue: (state) => {
      return (state.fareItems.unused_off_peak_demand?.amount || 0) > 0 || (state.fareItems.unused_peak_demand?.amount || 0) > 0
    },

    hasFreeMarket: (state) => {
      const { free_market } = state.freeMarketResult
      const usedConsumption = calculateItemValue(state.fareItems.peak_consumption)
      const usedPeakConsumption = calculateItemValue(state.fareItems.off_peak_consumption)
      const measuredPeakDemand = calculateItemValue(state.fareItems.measured_peak_demand)
      const measuredOffPeakDemand = calculateItemValue(state.fareItems.measured_off_peak_demand)

      return (free_market?.value || 0) < usedConsumption + usedPeakConsumption + measuredPeakDemand + measuredOffPeakDemand
    },

    hasReativeExcedent: (state) => {
      const reactive = (state.fareItems.peak_reactive?.amount || 0) > 0
      const offPeakReactive = (state.fareItems.off_peak_reactive?.amount || 0) > 0
      const exceeded = (state.fareItems.exceeded_peak_demand?.amount || 0) > 0 || (state.fareItems.exceeded_off_peak_demand?.amount || 0) > 0

      return reactive || exceeded || offPeakReactive
    },

    hasExceededDemand: (state) => {
      return (state.fareItems.exceeded_peak_demand?.amount || 0) > 0 || (state.fareItems.exceeded_off_peak_demand?.amount || 0) > 0
    },

    amountTotal: (state) => {
      return Object.values(state.fareItems).reduce((sum, item) => {
        return sum + calculateItemValue(item)
      }, 0)
    },

    freeMarket: (state) => {
      const normalMarketValue = state.freeMarketResult.normal_market?.value || 0
      const freeMarketValue = state.freeMarketResult.free_market?.value || 0
      const hasFreeMarket = freeMarketValue < normalMarketValue

      return hasFreeMarket ? freeMarketValue : 0
    },

    peakReative: (state) => {
      return calculateItemValue(state.fareItems.peak_reactive)
    },

    offPeakReative: (state) => {
      return calculateItemValue(state.fareItems.off_peak_reactive)
    },

    exceeded_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.exceeded_peak_demand)
    },

    exceeded_off_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.exceeded_off_peak_demand)
    },

    exceeded_reactive_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.exceeded_reactive_peak_demand)
    },

    exceeded_reactive_off_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.exceeded_reactive_off_peak_demand)
    },

    unused_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.unused_peak_demand)
    },

    unused_off_peak_demand: (state) => {
      return calculateItemValue(state.fareItems.unused_off_peak_demand)
    },

    potentialPeakSavings: (state) => {
      return state.peakWasteAnalysis?.potential_savings ?? 0
    },

    peakToOffpeakRatio: (state) => {
      return state.peakWasteAnalysis?.peak_to_offpeak_ratio ?? 0
    },

    hasHighPeakToOffpeakRatio: (state) => {
      return (state.peakWasteAnalysis?.peak_to_offpeak_ratio ?? 0) > 8
    },

    // Lista de oportunidades que vai para o relatório (editor + PDF). Enquanto o
    // diretor não tocar (curatedOpportunities === null), reflete a auto-detecção.
    reportOpportunities: (state): IReportOpportunity[] => {
      return state.curatedOpportunities ?? buildDefaultOpportunities(state.fareItems, state.freeMarketResult)
    },

    waterfallChartBars(): IWaterfallChartBar[] {
      if (this.isOpportunitiesCurated) {
        return buildWaterfallOpportunityBars(this.reportOpportunities)
      }
      return buildWaterfallChartBars(this.waterfallItems, this.fareItems)
    },

    // Mercado livre: barra paralela a fare_items, espelha reportOpportunities (curadoria).
    waterfallFreeMarketBar(): IWaterfallChartBar | null {
      return buildWaterfallFreeMarketBar(this.reportOpportunities)
    },

    waterfallPeakWasteBar(): IWaterfallChartBar | null {
      return buildWaterfallPeakWasteBar(this.potentialPeakSavings)
    },

    waterfallDeductionsTotal(): number {
      const fareTotal = this.waterfallChartBars.reduce((sum, bar) => sum + bar.value, 0)
      const freeMarket = this.waterfallFreeMarketBar?.value ?? 0
      const peakWaste = this.waterfallPeakWasteBar?.value ?? 0
      return fareTotal + freeMarket + peakWaste
    },

    waterfallSubtotal(): number {
      return Math.max(0, this.tariffTotalTable - this.waterfallDeductionsTotal)
    },

    // True quando o diretor já assumiu o controle da lista (há curadoria manual).
    isOpportunitiesCurated: (state): boolean => state.curatedOpportunities !== null,

    // Total mensal/anual das oportunidades que irão para o relatório.
    opportunitiesMonthlyTotal(): number {
      return this.reportOpportunities.reduce((sum, o) => sum + (o.economia_mensal || 0), 0)
    },

    // Pode gerar o relatório sempre que houver itens de fatura carregados. NÃO
    // depende do pdfBase64 (que não é persistido e é usado só para a miniatura da
    // fatura no PDF) — senão o botão fica travado após um reload mesmo com tudo
    // disponível na store.
    canGenerateReport: (state) => state.displayItems.length > 0,

    // Há inconsistências entre amount×preço e o valor da API (OCR pode ter errado).
    hasBlockingAlerts: (state) => state.validationAlerts.length > 0,

    // Abrir o diálogo de salvar: basta ter uma fatura analisada sem inconsistências.
    // O mês de referência é escolhido DENTRO do diálogo (não trava a abertura).
    canOpenSaveDialog: (state) => state.displayItems.length > 0 && state.validationAlerts.length === 0,

    // Confirmar o salvamento exige também o mês de referência selecionado.
    // Com 2+ UCs exige seleção; com 0 UCs a API pode criar a UC "padrao" sozinha.
    canSaveInvoice: (state) =>
      state.validationAlerts.length === 0 &&
      !!state.referenceMonth &&
      state.displayItems.length > 0 &&
      (state.consumerUnits.length < 2 || state.selectedConsumerUnitId != null),

    requiresConsumerUnitSelection: (state) => state.consumerUnits.length > 1 && state.selectedConsumerUnitId == null,

    showConsumerUnitSelector: (state) => state.consumerUnits.length > 1,

    needsConsumerUnitOnboarding: (state) => state.consumerUnits.length === 0,

    selectedConsumerUnit: (state): IEnergyConsumerUnit | null => {
      if (state.selectedConsumerUnitId == null) return null
      return state.consumerUnits.find((unit) => unit.id === state.selectedConsumerUnitId) ?? null
    },

    // Fatura já salva para o mês de referência selecionado (se a lista já foi
    // carregada). Usado para alertar antes de sobrescrever uma fatura completa.
    existingInvoiceForReferenceMonth: (state): IEnergyInvoiceSummary | undefined => {
      if (!state.referenceMonth) return undefined
      return state.savedInvoices.find((invoice) => invoice.reference_month?.slice(0, 7) === state.referenceMonth)
    },
  },

  actions: {
    initDisplayItems() {
      const itemsAux = []

      this.validationAlerts = []

      Object.entries(this.fareItems).forEach(([key, item]: [string, ItemDescription]) => {
        if (key === 'off_peak_injected_energy_gdi' && item.amount === 0) return
        if (key !== 'tariff_total' && item && (item.amount !== 0 || item.price_per_unit !== 0 || item.value !== 0)) {
          const { displayName, unit } = getDisplayInfo(key)
          const calculatedValue = item.amount * item.price_per_unit
          const hasError = !isValueWithinErrorMargin(calculatedValue, item.value)
          const isValueOnlyExpression = !item.amount && !item.price_per_unit

          if (hasError && !isValueOnlyExpression && isValueWithinErrorMargin(calculatedValue, item.value) === false) {
            this.addInvoiceAlert(
              `Erro no item "${displayName}": Valor calculado (${useCurrencyFormaterFromCents(
                calculatedValue,
              )}) difere do valor da API (${useCurrencyFormaterFromCents(item.value)})`,
            )
          }

          const itemDebug = {
            key: key as FareItemKey,
            displayName: displayName + (unit ? ` - ${unit}` : ''),
            amount: item.amount,
            pricePerUnit: item.price_per_unit,
            unit,
            valueFromAPI: item.value,
            hasError,
            isValueOnly: isValueOnlyExpression,
          }

          itemsAux.push(itemDebug)

          this.displayItems = itemsAux
        }
      })
    },

    async extractTariffItems(base64: string) {
      base64 = base64.replace(/^data:application\/pdf;base64,/, '')
      this.validationAlerts = []
      // Nova fatura → descarta a curadoria anterior; a lista volta a ser
      // auto-detectada a partir dos novos itens.
      this.curatedOpportunities = null
      this.waterfallItems = []
      this.pdfBase64 = base64

      const params: IPdfBase64Params = { pdf_base64: base64 }

      await fareOptimizerResource
        .extractPdfData(params)
        .then((response: { message: string; data: IExtractPdfDataResponse }) => {
          const tariffItems = response.data.tariff_items
          const adjustedData = { ...tariffItems }

          Object.keys(adjustedData).forEach((key) => {
            const item = adjustedData[key as keyof FareItems]
            if (item && item.value < 0 && item.amount > 0) {
              item.amount = -Math.abs(item.amount)
            }
          })

          if (adjustedData.off_peak_injected_energy_gdi) {
            adjustedData.off_peak_injected_energy_gdi.amount = -Math.abs(adjustedData.off_peak_injected_energy_gdi.amount || 0)
          }

          this.fareItems = createFareItems(adjustedData)
          this.historicalData = createHistoricalDataList(response.data.historical_data)
          if (response.data.peak_waste_analysis !== undefined) {
            this.peakWasteAnalysis = response.data.peak_waste_analysis
          }
          this.waterfallItems = response.data.waterfall_items ?? []
          this.initDisplayItems()
          this.freeMarketAnalysis()
          logFirebaseAnalytics(AnalyticsEvents.FARE_OPTIMIZER.ANALYSIS)
        })
        .catch(() => {
          // Erro ao enviar o PDF
        })

      return this.fareItems
    },

    async freeMarketAnalysis() {
      if (useCompanyStore().usesFreeMarket) {
        this.freeMarketResult = {} as IFreeMarketAnalysisResponse
        this.isLoadingOpportunity = false
        return
      }

      this.isLoadingOpportunity = true

      const params: IFreeMarketAnalysisParams = {
        tax: {
          contracted_peak_demand: this.fareItems.contracted_peak_demand?.amount || 0,
          contracted_off_peak_demand: this.fareItems.contracted_off_peak_demand?.amount || 0,
          peak_demand: this.fareItems.measured_peak_demand?.amount || 0,
          off_peak_demand: this.fareItems.measured_peak_demand?.amount || 0,
          peak_consumption: this.fareItems.peak_consumption?.amount || 0,
          off_peak_consumption: this.fareItems.off_peak_consumption?.amount || 0,
        },
      }

      await fareOptimizerResource
        .freeMarketAnalysis(params)
        .then((response) => {
          this.freeMarketResult = response.data
          this.isLoadingOpportunity = false
        })
        .catch(() => {
          this.isLoadingOpportunity = false
        })
    },

    addInvoiceAlert(alert: string) {
      this.validationAlerts.push(alert)
    },

    cleanInvoiceAlerts() {
      this.validationAlerts = []
    },

    updateFareItem(key: string, item: ItemDescription) {
      this.fareItems = {
        ...this.fareItems,
        [key]: item,
      }
    },

    // --- Curadoria das oportunidades do relatório (diretor inclui/remove/edita) ---

    // "Materializa" a lista auto-detectada em uma cópia editável na primeira vez
    // que o diretor mexe nela. A partir daí a curadoria é a fonte da verdade.
    materializeOpportunities() {
      if (this.curatedOpportunities === null) {
        this.curatedOpportunities = this.reportOpportunities.map((o) => ({ ...o }))
      }
    },

    addOpportunity(partial: Partial<IReportOpportunity> = {}) {
      this.materializeOpportunities()
      this.curatedOpportunities!.push(buildOpportunity(partial))
    },

    updateOpportunity(index: number, patch: Partial<IReportOpportunity>) {
      this.materializeOpportunities()
      const current = this.curatedOpportunities![index]
      if (!current) return

      const next = { ...current, ...patch }
      // Ao editar a economia mensal, recalculamos a anual (12×) — a menos que ela
      // venha explicitamente no patch (edição manual da anual).
      if (patch.economia_mensal != null && patch.economia_anual == null) {
        next.economia_anual = next.economia_mensal * 12
      }
      this.curatedOpportunities![index] = buildOpportunity(next)
    },

    removeOpportunity(index: number) {
      this.materializeOpportunities()
      this.curatedOpportunities!.splice(index, 1)
    },

    // Volta a seguir a auto-detecção (descarta a curadoria manual).
    resetOpportunities() {
      this.curatedOpportunities = null
    },

    // Gera o relatório PDF de Otimização de Fatura no backend e dispara o
    // download. Envia o estado atual dos itens (com edições manuais do usuário)
    // como overrides; o backend reextrai o PDF e aplica essas alterações.
    async generateReport() {
      // pdfBase64 é opcional (só a miniatura da fatura). O relatório precisa de
      // itens de fatura — sem eles, não há o que renderizar.
      if (this.displayItems.length === 0) return

      this.isGeneratingReport = true

      try {
        const params: IFareReportParams = {
          fare_items: this.fareItems,
          free_market_result: this.freeMarketResult,
          historical_data: this.historicalData,
          // Enviado só quando há fatura carregada nesta sessão (não é persistido).
          pdf_base64: this.pdfBase64 || undefined,
          // Lista curada na tela (ou a auto-detecção, se o diretor não mexeu).
          opportunities: withPeakWasteOpportunity(this.reportOpportunities, this.potentialPeakSavings),
        }

        const response = await fareOptimizerResource.generateReport(params)

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const disposition = (response.headers?.['content-disposition'] as string | undefined) ?? ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const filename = match?.[1] || 'relatorio-otimizacao-fatura.pdf'

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        logFirebaseAnalytics(AnalyticsEvents.FARE_OPTIMIZER.ANALYSIS)
      } catch (error) {
        useErrorStore().setErrors([reportErrorMessage(error, i18n.global.t('contract_optimizer.report.error'))])
      } finally {
        this.isGeneratingReport = false
      }
    },

    setReferenceMonth(month: string) {
      this.referenceMonth = month
    },

    setInvoiceMetadata(meta: { distribuidora?: string }) {
      if (meta.distribuidora !== undefined) this.distribuidora = meta.distribuidora
    },

    async fetchConsumerUnits() {
      this.isLoadingConsumerUnits = true
      try {
        const body = await energyConsumerUnitResource.list({ active: true })
        this.consumerUnits = unwrapList<IEnergyConsumerUnit>(body)

        if (this.consumerUnits.length === 0) {
          this.selectedConsumerUnitId = null
          this.savedInvoices = []
          return
        }

        const stillSelected =
          this.selectedConsumerUnitId != null &&
          this.consumerUnits.some((unit) => unit.id === this.selectedConsumerUnitId)

        if (this.consumerUnits.length === 1) {
          this.selectedConsumerUnitId = this.consumerUnits[0].id
        } else if (!stillSelected) {
          this.selectedConsumerUnitId = null
        }

        if (this.selectedConsumerUnitId != null) {
          await this.fetchInvoices()
        } else {
          this.savedInvoices = []
        }
      } catch {
        this.consumerUnits = []
        this.selectedConsumerUnitId = null
        this.savedInvoices = []
      } finally {
        this.isLoadingConsumerUnits = false
      }
    },

    async selectConsumerUnit(id: number) {
      if (this.selectedConsumerUnitId === id) return
      this.selectedConsumerUnitId = id
      this.selectedInvoice = null
      this.savedInvoices = []
      await this.fetchInvoices()
    },

    async createConsumerUnit(params: ICreateEnergyConsumerUnitParams): Promise<IEnergyConsumerUnit | null> {
      try {
        const body = await energyConsumerUnitResource.create(params)
        const created = unwrapRecord<IEnergyConsumerUnit>(body)
        if (created?.id == null) return null
        await this.fetchConsumerUnits()
        await this.selectConsumerUnit(created.id)
        return this.selectedConsumerUnit
      } catch {
        return null
      }
    },

    async updateConsumerUnit(id: number, params: IUpdateEnergyConsumerUnitParams): Promise<IEnergyConsumerUnit | null> {
      try {
        const body = await energyConsumerUnitResource.update(id, params)
        const updated = unwrapRecord<IEnergyConsumerUnit>(body)
        await this.fetchConsumerUnits()
        return this.consumerUnits.find((unit) => unit.id === id) ?? updated
      } catch {
        return null
      }
    },

    async alignConsumerUnitFromInvoiceResponse(responseBody: unknown) {
      const invoice = unwrapRecord<IEnergyInvoiceSummary>(responseBody)
      const responseUnitId = invoice?.energy_consumer_unit_id
      await this.fetchConsumerUnits()
      if (responseUnitId != null && responseUnitId !== this.selectedConsumerUnitId) {
        await this.selectConsumerUnit(responseUnitId)
      } else if (this.selectedConsumerUnitId != null) {
        await this.fetchInvoices()
      }
    },

    // Salva a fatura (dados já corrigidos) vinculada ao mês de referência. NÃO
    // envia o PDF. Bloqueia se houver inconsistências pendentes (a menos que o
    // usuário force). Retorna true em caso de sucesso.
    async saveInvoice(opts: { forceOverwrite?: boolean } = {}): Promise<boolean> {
      if (this.hasBlockingAlerts && !opts.forceOverwrite) return false
      if (!this.referenceMonth) return false
      if (this.requiresConsumerUnitSelection) return false

      this.isSavingInvoice = true
      try {
        const params: ISaveInvoiceParams = {
          reference_month: this.referenceMonth,
          fare_items: this.fareItems,
          historical_data: this.historicalData,
          ...(this.selectedConsumerUnitId != null
            ? { energy_consumer_unit_id: this.selectedConsumerUnitId }
            : {}),
          distribuidora: this.distribuidora || undefined,
          source: 'pdf_ocr',
        }

        const body = await energyInvoiceResource.save(params)
        logFirebaseAnalytics(AnalyticsEvents.FARE_OPTIMIZER.SAVE_INVOICE)
        await this.alignConsumerUnitFromInvoiceResponse(body)
        return true
      } catch (error) {
        // Erros de rede/validação já são exibidos pelo interceptor do axios.
        return false
      } finally {
        this.isSavingInvoice = false
      }
    },

    // Cadastro manual de uma fatura antiga (sem PDF). Retorna true em sucesso.
    async saveManualInvoice(params: IManualInvoiceParams): Promise<boolean> {
      if (this.requiresConsumerUnitSelection) return false

      this.isSavingInvoice = true
      try {
        const payload: IManualInvoiceParams = {
          ...params,
          ...(this.selectedConsumerUnitId != null && params.energy_consumer_unit_id == null
            ? { energy_consumer_unit_id: this.selectedConsumerUnitId }
            : {}),
        }
        const body = await energyInvoiceResource.saveManual(payload)
        logFirebaseAnalytics(AnalyticsEvents.FARE_OPTIMIZER.SAVE_INVOICE)
        await this.alignConsumerUnitFromInvoiceResponse(body)
        return true
      } catch (error) {
        return false
      } finally {
        this.isSavingInvoice = false
      }
    },

    async fetchInvoices() {
      // Sem UC: a listagem exige escopo; o 1º save cria a UC "padrao" na API.
      // Com 2+ UCs sem seleção: também não há escopo válido.
      if (
        this.consumerUnits.length === 0 ||
        (this.consumerUnits.length > 1 && this.selectedConsumerUnitId == null)
      ) {
        this.savedInvoices = []
        return
      }

      this.isLoadingInvoices = true
      try {
        // render_success envia o payload direto (sem envelope { data }); o
        // ApiService já devolve o corpo. Aceitamos array puro ou { data: [] }.
        const body = await energyInvoiceResource.list(
          this.selectedConsumerUnitId != null
            ? { energy_consumer_unit_id: this.selectedConsumerUnitId }
            : undefined,
        )
        this.savedInvoices = unwrapList<IEnergyInvoiceSummary>(body)
      } catch (error) {
        this.savedInvoices = []
      } finally {
        this.isLoadingInvoices = false
      }
    },

    async fetchInvoiceDetail(id: number) {
      try {
        const body = await energyInvoiceResource.get(id)
        this.selectedInvoice = (body?.data ?? body) as IEnergyInvoice
      } catch (error) {
        this.selectedInvoice = null
      }
      return this.selectedInvoice
    },

    async deleteInvoice(id: number) {
      await energyInvoiceResource.delete(id)
      this.savedInvoices = this.savedInvoices.filter((invoice) => invoice.id !== id)
    },

    // Exclui várias faturas de uma vez (seleção de linhas no histórico). Remove da
    // lista apenas as que realmente foram apagadas; devolve o total excluído.
    async deleteInvoices(ids: number[]): Promise<number> {
      if (ids.length === 0) return 0

      this.isLoadingInvoices = true
      try {
        const results = await Promise.allSettled(ids.map((id) => energyInvoiceResource.delete(id)))
        const deletedIds = ids.filter((_, index) => results[index].status === 'fulfilled')
        this.savedInvoices = this.savedInvoices.filter((invoice) => !deletedIds.includes(invoice.id))
        return deletedIds.length
      } finally {
        this.isLoadingInvoices = false
      }
    },
  },
})
